import React, { useState, useEffect } from 'react';
import { useEntraAuth } from '../components/EntraAuth/EntraAuthProvider';
import { EntraLoginButton } from '../components/EntraAuth/EntraLoginButton';
import { Card } from '../components/ui';
import RedirectOverlay from '../components/RedirectOverlay';
import './LoginEntra.css';

const LoginEntraContent = () => {
    const { isAuthenticated, user, loading, login } = useEntraAuth();

    // State for messages
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);

    // Auto-login when coming from Login page
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const autoLogin = urlParams.get('autoLogin');
        const loginHint = urlParams.get('loginHint');

        // Only trigger auto-login once when autoLogin param is present
        if (autoLogin === 'true' && !autoLoginAttempted && !isAuthenticated) {
            setAutoLoginAttempted(true);
            // Trigger persistent overlay before starting Microsoft authentication
            if (window.triggerMicrosoftOverlay) {
                window.triggerMicrosoftOverlay();
            }
            // Small delay to ensure overlay is visible before login
            setTimeout(() => {
                login(loginHint);
            }, 100);
        } else if (autoLogin === 'true' && isAuthenticated && !autoLoginAttempted) {
            // Already authenticated, redirect immediately to Compas with overlay
            setAutoLoginAttempted(true);
            setSuccess('Already authenticated! Redirecting...');
            const compasUrl = process.env.REACT_APP_COMPAS_URL || 'https://compas.evoomedical.ro';
            setTimeout(() => {
                window.location.href = `${compasUrl}?autoLogin=true`;
            }, 2000);
        }
    }, [isAuthenticated, autoLoginAttempted, login, loading]);

    // Check authentication status on component mount (no auto-redirect)
    useEffect(() => {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (token && storedUser) {
            try {
                const userData = JSON.parse(storedUser);
                setSuccess(`Welcome back, ${userData.name || userData.email}!`);
            } catch (parseError) {
                console.error('Error parsing stored user data:', parseError);
                setError('Invalid stored authentication data. Please login again.');
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('authType');
            }
        }
    }, []);

    // Show success message when authenticated and redirect to Compas
    useEffect(() => {
        if (isAuthenticated && user && !loading) {
            setError('');
            setSuccess('Microsoft authentication successful! Redirecting...');

            // Redirect to Compas after a short delay
            const compasUrl = process.env.REACT_APP_COMPAS_URL || 'https://compas.evoomedical.ro';
            const redirectTimeout = setTimeout(() => {
                window.location.href = `${compasUrl}?autoLogin=true`;
            }, 2000);

            return () => clearTimeout(redirectTimeout);
        }
    }, [isAuthenticated, user, loading]);

    // Handle Entra ID login error
    const handleEntraLoginError = (error) => {
        console.error('Entra login failed:', error);
        const errorMessage = error?.message || error?.errorMessage || 'Authentication failed. Please try again.';
        setError(`Authentication failed: ${errorMessage}`);
        setSuccess('');
    };

    // Check if we're in auto-login mode
    const urlParams = new URLSearchParams(window.location.search);
    const isAutoLogin = urlParams.get('autoLogin') === 'true';

    // If auto-login and not authenticated yet, show blank screen while redirecting to Microsoft
    if (isAutoLogin && !isAuthenticated && !success) {
        return <div className="login-entra-container"></div>;
    }

    return (
        <div className="login-entra-container">
            <div className="login-entra-card">
                {/* Header */}
                <div className="login-entra-header">
                    <a className="home-link" title="Înapoi la pagina principală" href="https://evoomedical.ro">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                            <polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></polyline>
                        </svg>
                    </a>
                    <h1 className="login-entra-title">Evoo Medical Compas</h1>
                </div>

                {/* Error Message */}
                {error && (
                    <Card className="error-message">
                        <div className="message-content">
                            <span className="message-icon">⚠️</span>
                            <span>{error}</span>
                        </div>
                    </Card>
                )}

                {/* Success/Loading Message with Animation */}
                <RedirectOverlay
                    show={!!success}
                    userName={user?.name || 'utilizator'}
                    message={success.includes('curs') ? "Vă conectăm la Microsoft Entra ID..." : "Vă redirecționăm către Evoo Medical Compas..."}
                    type={success.includes('curs') ? 'loading' : 'success'}
                />

                {/* Microsoft Login Section */}
                {!isAuthenticated && !loading && !autoLoginAttempted && !new URLSearchParams(window.location.search).get('autoLogin') && (
                    <div className="microsoft-login-section">
                        <h3 className="microsoft-login-title">Autentificare Evoo Medical</h3>

                        <p className="microsoft-login-description">
                            Folosiți contul dvs. Microsoft Entra ID pentru a accesa în siguranță platforma Evoo Medical.
                        </p>

                        <EntraLoginButton
                            onLoginError={handleEntraLoginError}
                        />
                    </div>
                )}

                {/* Navigation Options */}
                <div className="navigation-section">
                    <div className="login-info">
                        <h4>Informații despre autentificare:</h4>
                        <ul>
                            <li><strong> Medici și angajați:</strong> Folosiți autentificarea Microsoft pentru acces securizat la sistemul medical</li>
                            <li><strong> Pacienți:</strong> Pentru programări online, contactați clinica pentru asistență</li>
                        </ul>
                        <p className="login-note">Dacă întâmpinați probleme cu autentificarea, <a href="mailto:webadmin@evoomedical.ro">contactați</a> administratorul sistemului.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const LoginEntra = () => {
    return <LoginEntraContent />;
};

export default LoginEntra;
